import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RoomService } from '../../../../services/roomService';

import './ComparaHabitaciones.css';
import OtherRoomsSection from '../../../organisms/OtherRoomsSection/OtherRoomsSection';
import heroComparaImg from '../../../../assets/hero-compara.png';

// --- IMPORTACIÓN DE TUS ICONOS SVG ---
import IconVistaMar from '../../../../assets/icons/icon-vista-mar.png';
import IconAireAcondicionado from '../../../../assets/icons/icon-aire-acondicionado.png';
import IconCajaFuerte from '../../../../assets/icons/icon-caja-fuerte.png';
import IconFrigobar from '../../../../assets/icons/icon-frigobar.png';
import IconHamaca from '../../../../assets/icons/icon-hamacas.png';
import IconJacuzzi from '../../../../assets/icons/icon-jacuzzi.png';
import IconKingSize from '../../../../assets/icons/icon-king-size.png';
import IconRegadera from '../../../../assets/icons/icon-regadera.png';
import IconTerraza from '../../../../assets/icons/icon-terraza.png';
import IconVistaParcial from '../../../../assets/icons/icon-vista-parcial.png';
import IconQueen from '../../../../assets/icons/icon-queen.png';
import IconAlberca from '../../../../assets/icons/icon-alberca.png';
import IconDetector from '../../../../assets/icons/icon-detector.png';
import IconTelefono from '../../../../assets/icons/icon-telefono.png';
import IconTV from '../../../../assets/icons/icon-tv.png';

// Mapa de Iconos
const iconMapping = {
  'vista al mar': IconVistaMar,
  'ocean view': IconVistaMar,
  'vista parcial': IconVistaParcial,
  'aire acondicionado': IconAireAcondicionado,
  'air conditioning': IconAireAcondicionado,
  'caja fuerte': IconCajaFuerte,
  'safe': IconCajaFuerte,
  'minibar': IconFrigobar,
  'frigobar': IconFrigobar,
  'hamaca': IconHamaca,
  'hammocks': IconHamaca,
  'jacuzzi': IconJacuzzi,
  'cama king': IconKingSize,
  'king size': IconKingSize,
  'cama queen': IconQueen,
  'queen bed': IconQueen,
  'regadera': IconRegadera,
  'shower': IconRegadera,
  'terraza': IconTerraza,
  'terrace': IconTerraza,
  'alberca': IconAlberca,
  'piscina': IconAlberca,
  'pool': IconAlberca,
  'detector': IconDetector,
  'telefono': IconTelefono,
  'telephone': IconTelefono,
  'tv': IconTV,
  'television': IconTV
};

const ComparaHabitaciones = () => {
  const { i18n } = useTranslation();
  const { openBooking } = useOutletContext();

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const [leftRoomSlug, setLeftRoomSlug] = useState('');
  const [rightRoomSlug, setRightRoomSlug] = useState('');

  // 1. CARGAR DATOS
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const currentLang = i18n.language.split('-')[0];
        const data = await RoomService.getAll(currentLang);

        if (data && data.length > 0) {
          setRooms(data);
          setLeftRoomSlug(data[0].slug);
          setRightRoomSlug(data[1] ? data[1].slug : data[0].slug);
        }
      } catch (error) {
        console.error("Error cargando habitaciones:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [i18n.language]);

  // 2. HELPER FEATURE
  const findFeature = (room, keywords, defaultIcon, defaultLabel, overrideValue = null) => {
    if (!room) return null;

    const foundAmenity = room.amenities.find(a =>
      keywords.some(k => a.name.toLowerCase().includes(k.toLowerCase()))
    );

    if (foundAmenity) {
      let finalIcon = defaultIcon;
      if (!finalIcon) {
        const key = Object.keys(iconMapping).find(k => foundAmenity.name.toLowerCase().includes(k));
        if (key) finalIcon = iconMapping[key];
      }
      return {
        icon: finalIcon,
        label: foundAmenity.name,
        value: overrideValue || ''
      };
    }
    return null;
  };

  // 3. PROCESAR ROOM
  const getProcessedRoom = (slug) => {
    const room = rooms.find(r => r.slug === slug);
    if (!room) return null;

    const bedLower = room.specs.bed.toLowerCase();
    const bedIcon = bedLower.includes('king') ? IconKingSize : IconQueen;

    return {
      name: room.title,
      // description: room.description, // Eliminada por solicitud
      images: room.images.map(img => img.url),
      features: {
        vista: findFeature(room, ['vista', 'view'], null, 'Vista') || { icon: IconVistaMar, label: 'Vista' },

        distancia: {
          icon: "",
          value: room.specs.distance_sea,
          label: i18n.language.startsWith('en') ? 'Distance to sea' : 'Distancia al mar'
        },
        superficie: {
          icon: "",
          value: room.specs.surface,
          label: i18n.language.startsWith('en') ? 'Surface' : 'Superficie'
        },
        piso: {
          icon: "",
          value: room.specs.location,
          label: i18n.language.startsWith('en') ? 'Location' : 'Ubicación'
        },
        niveles: null,

        cama: { icon: bedIcon, value: '', label: room.specs.bed },
        capacidad: {
          icon: "",
          value: room.specs.capacity.toString(),
          label: i18n.language.startsWith('en') ? 'Max capacity' : 'Capacidad máxima'
        },

        terraza: findFeature(room, ['terraza', 'terrace'], IconTerraza, 'Terraza'),
        regadera: findFeature(room, ['regadera', 'shower', 'ducha'], IconRegadera, 'Regadera'),
        alberca: findFeature(room, ['alberca', 'piscina', 'pool'], IconAlberca, 'Alberca'),
        jacuzzi: findFeature(room, ['jacuzzi', 'hot tub'], IconJacuzzi, 'Jacuzzi'),
        aire: findFeature(room, ['aire', 'air condition'], IconAireAcondicionado, 'Aire Acondicionado'),
        caja: findFeature(room, ['caja', 'safe', 'security'], IconCajaFuerte, 'Caja Fuerte'),
        hamaca: findFeature(room, ['hamaca', 'hammock'], IconHamaca, 'Hamaca'),
        frigobar: findFeature(room, ['frigobar', 'minibar'], IconFrigobar, 'Frigobar'),
        humo: findFeature(room, ['humo', 'smoke'], IconDetector, 'Detector Humo'),
        telefono: findFeature(room, ['telefono', 'phone'], IconTelefono, 'Teléfono'),
        tv: findFeature(room, ['tv', 'television', 'cable'], IconTV, 'TV'),
      }
    };
  };

  const leftRoom = getProcessedRoom(leftRoomSlug);
  const rightRoom = getProcessedRoom(rightRoomSlug);

  // --- SUB-COMPONENTES ---

  const FeatureItem = ({ feature, labelDefault }) => {
    if (!feature) {
      return (
        <div className="compara-feature-item not-included">
          <div className="compara-icon">
            <span className="legend-line"></span>
          </div>
          <div className="compara-feature-text">
            <span className="compara-feature-label dimmed">{labelDefault}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="compara-feature-item">
        <div className="compara-icon">
          {feature.icon ? (
            <img src={feature.icon} alt={feature.label || labelDefault} className="compara-svg-icon" />
          ) : (
            <div className="icon-placeholder"></div>
          )}
        </div>
        <div className="compara-feature-text">
          {feature.value && <p className="compara-feature-value">{feature.value}</p>}
          <span className="compara-feature-label">{feature.label || labelDefault}</span>
        </div>
      </div>
    );
  };

  const ComparisonColumn = ({ selectedSlug, onSelect, roomData, isReverse }) => {
    if (!roomData) return <div className="compara-column">Cargando...</div>;

    return (
      <div className="compara-column">
        {/* Selector */}
        <div className="compara-select-wrapper">
          <select value={selectedSlug} onChange={(e) => onSelect(e.target.value)}>
            {rooms.map(r => (
              <option key={r.slug} value={r.slug}>{r.title}</option>
            ))}
          </select>
        </div>

        <div className={`compara-body ${isReverse ? 'layout-reverse' : 'layout-normal'}`}>

          {/* Lado A: TODAS las Imágenes */}
          <div className="compara-images-col">

            {/* Si hay imágenes, las mapeamos todas */}
            {roomData.images && roomData.images.length > 0 ? (
              roomData.images.map((imgUrl, index) => (
                <div
                  key={index}
                  className={`compara-image-container ${index === 0 ? 'main-image' : 'secondary-image'}`}
                >
                  <img src={imgUrl} alt={`${roomData.name} ${index + 1}`} />
                </div>
              ))
            ) : (
              // Fallback si no hay imágenes
              <div className="compara-image-container main-image">
                <img src="https://via.placeholder.com/600" alt="No image" />
              </div>
            )}

          </div>

          {/* Lado B: Lista de Amenidades */}
          <div className="compara-features-col">

            <FeatureItem feature={roomData.features.vista} labelDefault="Vista" />
            <FeatureItem feature={roomData.features.distancia} labelDefault="Distancia" />
            <FeatureItem feature={roomData.features.superficie} labelDefault="Superficie" />
            <FeatureItem feature={roomData.features.piso} labelDefault="Ubicación" />
            <FeatureItem feature={roomData.features.niveles} labelDefault="Niveles" />
            <FeatureItem feature={roomData.features.cama} labelDefault="Camas" />
            <FeatureItem feature={roomData.features.capacidad} labelDefault="Capacidad" />

            <FeatureItem feature={roomData.features.terraza} labelDefault="Terraza privada" />
            <FeatureItem feature={roomData.features.regadera} labelDefault="Regadera exterior" />
            <FeatureItem feature={roomData.features.alberca} labelDefault="Alberca Privada" />
            <FeatureItem feature={roomData.features.jacuzzi} labelDefault="Jacuzzi Privado" />
            <FeatureItem feature={roomData.features.aire} labelDefault="Aire acondicionado" />
            <FeatureItem feature={roomData.features.caja} labelDefault="Caja fuerte" />
            <FeatureItem feature={roomData.features.hamaca} labelDefault="Hamaca" />
            <FeatureItem feature={roomData.features.frigobar} labelDefault="Frigobar" />
            <FeatureItem feature={roomData.features.humo} labelDefault="Detector de humo" />
            <FeatureItem feature={roomData.features.telefono} labelDefault="Teléfono interno" />
            <FeatureItem feature={roomData.features.tv} labelDefault="Televisión" />

            <div className="compara-button-container">
              <button
                className="compara-btn-reservar"
                onClick={openBooking}
              >
                Reservar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div className="text-center py-20">Cargando comparación...</div>;

  return (
    <div className="compara-page">
      <div className="compara-hero">
        <div className="compara-hero-overlay"></div>
        <img src={heroComparaImg} alt="Hero" className="compara-hero-bg" />
        <h1 className="compara-hero-title">Compara Habitaciones</h1>
      </div>

      <div className="compara-container">
        <div className="compara-header">
          <h2>COMPARA LAS HABITACIONES</h2>
          <p>
            Nuestras categorías cuentan con amenidades diferentes.<br />
            <strong>Selecciona la ideal para ti.</strong>
          </p>

          <div className="compara-legend">
            <span className="legend-line"></span>
            <span className="legend-text">No incluye</span>
          </div>
        </div>

        <div className="compara-grid">
          <ComparisonColumn
            selectedSlug={leftRoomSlug}
            onSelect={setLeftRoomSlug}
            roomData={leftRoom}
            isReverse={false}
          />
          <ComparisonColumn
            selectedSlug={rightRoomSlug}
            onSelect={setRightRoomSlug}
            roomData={rightRoom}
            isReverse={true}
          />
        </div>

        <div className="linea-global"></div>
        <OtherRoomsSection />
      </div>
    </div>
  );
};

export default ComparaHabitaciones;